/** @jsxImportSource @emotion/react */
import { compact } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { ApiOrgProduct } from '@kaiizen/kai-pro-api';
import { useAllProductSupplierOrgs, useCombinedProductSearch, useOrgProduct } from '@kaiizen/kai-pro-api-react-state';
import { useEffect, useMemo, useState } from 'react';

import {
  CombinedOrgProductRetailerProduct,
  getCombinedOrgProductFromCombinedSearchResponse,
  getOrgProductIsAvailable,
  getProductSearchResultSource,
  getProductSupplierOrgIdForNewOrgProduct,
} from '@/Client/Lib';
import { formatUsd } from '@/Client/Presenters';
import { LAYOUT2 } from '@/Client/Styles';
import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { ModalShell } from '@/Client/Widgets';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { CloseIcon, PhotoIcon } from '@kaiizen/kai-shiki/react';
import { FocusedOrgProductDetails } from './FocusedOrgProductDetails';

/** The product search runs once a query is this long. */
const MIN_QUERY_LENGTH = 3;

const DIALOG_STYLE = css`
  label: DIALOG_STYLE;
  display: flex;
  flex-direction: column;
  height: min(820px, calc(100vh - 80px));
  background: ${COLORS.white};
  border: 1px solid ${COLORS.ink};
  position: relative;
`;

const CLOSE_BUTTON_STYLE = css`
  label: CLOSE_BUTTON_STYLE;
  position: absolute;
  top: 18px;
  right: 18px;
  padding: 6px;
  background: none;
  border: 0;
  cursor: pointer;
  line-height: 0;
  color: ${COLORS.midtone};

  &:hover {
    color: ${COLORS.ink};
  }
`;

const HEAD_STYLE = css`
  label: HEAD_STYLE;
  flex-shrink: 0;
  padding: 24px 28px 0;
`;

const EYEBROW_STYLE = css`
  label: EYEBROW_STYLE;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${COLORS.ink};
`;

const TITLE_STYLE = css`
  label: TITLE_STYLE;
  margin: ${LAYOUT2.spacing1p5}px 0 0;
  padding-right: 28px;
  font-family: ${FONT.display};
  font-size: ${pxToRem(22)};
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  color: ${COLORS.ink};
`;

const SEARCH_BAR_STYLE = css`
  label: SEARCH_BAR_STYLE;
  flex-shrink: 0;
  display: flex;
  gap: 8px;
  padding: ${LAYOUT2.spacing2}px 28px;
  border-bottom: 1px solid ${COLORS.stroke};
`;

const SEARCH_INPUT_STYLE = css`
  label: SEARCH_INPUT_STYLE;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  padding: ${LAYOUT2.spacing1}px;
  font-family: ${FONT.body};
  font-size: ${pxToRem(13.5)};
  color: ${COLORS.ink};
  background: ${COLORS.white};
  border: 1px solid ${COLORS.stroke};
  outline: none;

  &:focus {
    border-color: ${COLORS.ink};
    box-shadow: inset 0 0 0 1px ${COLORS.ink};
  }
`;

const SEARCH_BUTTON_STYLE = css`
  label: SEARCH_BUTTON_STYLE;
  padding: 0 ${LAYOUT2.spacing2}px;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.white};
  background: ${COLORS.ink};
  border: 1px solid ${COLORS.ink};
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const PANES_STYLE = css`
  label: PANES_STYLE;
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
`;

const RESULTS_STYLE = css`
  label: RESULTS_STYLE;
  min-height: 0;
  overflow-y: auto;
`;

const DETAILS_PANE_STYLE = css`
  label: DETAILS_PANE_STYLE;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-left: 1px solid ${COLORS.stroke};
  background: ${COLORS.paper};
`;

const DETAILS_STYLE = css`
  label: DETAILS_STYLE;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${LAYOUT2.spacing2}px;
`;

const DETAILS_ACTIONS_STYLE = css`
  label: DETAILS_ACTIONS_STYLE;
  flex-shrink: 0;
  padding: ${LAYOUT2.spacing1p5}px ${LAYOUT2.spacing2}px;
  border-top: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};
`;

const ACTION_BUTTON_STYLE = css`
  label: ACTION_BUTTON_STYLE;
  width: 100%;
  min-height: 40px;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(11)};
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.white};
  background: ${COLORS.ink};
  border: 1px solid ${COLORS.ink};
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const STATUS_STYLE = css`
  label: STATUS_STYLE;
  padding: ${LAYOUT2.spacing4}px 28px;
  text-align: center;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.midtone};
`;

const RESULT_STYLE = css`
  label: RESULT_STYLE;
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  gap: ${LAYOUT2.spacing1p5}px;
  align-items: center;
  width: 100%;
  padding: 12px 28px;
  text-align: left;
  background: none;
  border: 0;
  border-bottom: 1px solid ${COLORS.stroke};
  cursor: pointer;

  &:hover {
    background: ${COLORS.paper};
  }
`;

const RESULT_SELECTED_STYLE = css`
  label: RESULT_SELECTED_STYLE;
  background: ${COLORS.selectedWash};
  box-shadow: inset 3px 0 0 ${COLORS.ink};

  &:hover {
    background: ${COLORS.selectedWash};
  }
`;

const RESULT_PHOTO_STYLE = css`
  label: RESULT_PHOTO_STYLE;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  overflow: hidden;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.stroke};
  color: ${COLORS.midtone};

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RESULT_TITLE_STYLE = css`
  label: RESULT_TITLE_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(13.5)};
  font-weight: 700;
  line-height: 1.3;
  color: ${COLORS.ink};
`;

const RESULT_DETAILS_STYLE = css`
  label: RESULT_DETAILS_STYLE;
  margin-top: 4px;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9.5)};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.midtone};
`;

const RESULT_PRICE_STYLE = css`
  label: RESULT_PRICE_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(13.5)};
  font-weight: 500;
  color: ${COLORS.ink};
  white-space: nowrap;
`;

const ResultPhoto = ({ image }: { image: string | undefined }) => {
  const [photoFailed, setPhotoFailed] = useState(false);
  return (
    <div css={RESULT_PHOTO_STYLE}>
      {image && !photoFailed ? <img src={image} alt='' onError={() => setPhotoFailed(true)} /> : <PhotoIcon />}
    </div>
  );
};

/**
 * The focused scope editor's product search: the org's products and the supplier catalog in one
 * list, unavailable products left out except the line's current product. Selecting a result shows
 * its details beside the list, under the action that takes it; an unavailable product can't be taken. Taking an org product hands it straight back; taking a catalog product
 * first makes it an org product (the server returns the org's existing one for that supplier SKU),
 * the same way the template editor adds products.
 */
export const FocusedProductSearchDialog = ({
  isOpen,
  onClose,
  onAdd,
  currentProduct,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (orgProduct: ApiOrgProduct) => void;
  /** The line's product, if any: each opening searches for its title and selects it, and taking a result reads "Replace product". */
  currentProduct?: ApiOrgProduct;
}) => {
  const initialSearch = currentProduct?.title;
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<CombinedOrgProductRetailerProduct>();
  const [adding, setAdding] = useState(false);
  const { productSupplierOrgs } = useAllProductSupplierOrgs();
  const homeDepotOrgId = productSupplierOrgs?.homeDepot?.id;
  const { results, isLoading } = useCombinedProductSearch(searchQuery);
  const { orgProductMutators } = useOrgProduct(undefined);

  useEffect(() => {
    if (!isOpen || !initialSearch) return;
    setSearchInputValue(initialSearch);
    setSearchQuery(initialSearch.trim());
    setSelectedProduct(currentProduct);
  }, [isOpen, initialSearch, currentProduct]);

  // the org's own products and catalog V2 results say who supplies them; other catalog results are Home Depot's
  const products = useMemo(() =>
    results
      .map(result => getCombinedOrgProductFromCombinedSearchResponse(result))
      .map(product => product.orgId || product.productSupplierOrgId ? product : { ...product, productSupplierOrgId: homeDepotOrgId })
      .filter(product => getOrgProductIsAvailable(product, homeDepotOrgId) || (!!product.id && product.id === currentProduct?.id)), [
    results,
    homeDepotOrgId,
    currentProduct?.id,
  ]);

  const getProductKey = (product: CombinedOrgProductRetailerProduct) => product.id ?? product.sku;
  const selectedProductIsAvailable = !!selectedProduct && getOrgProductIsAvailable(selectedProduct, homeDepotOrgId);

  const addProduct = async (product: CombinedOrgProductRetailerProduct) => {
    if (adding) return;
    if (product.orgId && product.id) {
      onAdd(product as ApiOrgProduct);
    } else {
      const productSupplierOrgId = getProductSupplierOrgIdForNewOrgProduct(product, homeDepotOrgId);
      if (!productSupplierOrgId) return;
      setAdding(true);
      try {
        onAdd(await orgProductMutators.create({ productSupplierOrgId, productSupplierSku: product.sku }));
      } finally {
        setAdding(false);
      }
    }
    onClose();
  };

  const search = () => {
    setSearchQuery(searchInputValue.trim());
    setSelectedProduct(undefined);
  };
  const status = searchQuery.length < MIN_QUERY_LENGTH
    ? `Search by title, description, or SKU (${MIN_QUERY_LENGTH}+ characters)`
    : isLoading
    ? 'Searching…'
    : products.length === 0
    ? 'No available products match'
    : undefined;

  return (
    <ModalShell visible={isOpen} onClose={onClose} contentMaxWidth='1080px'>
      <div css={DIALOG_STYLE} role='dialog' aria-modal='true' aria-label='Choose a product' data-testid='focused-product-search-dialog'>
        <button type='button' css={CLOSE_BUTTON_STYLE} aria-label='Close' onClick={onClose}>
          <CloseIcon size={14} />
        </button>
        <div css={HEAD_STYLE}>
          <span css={EYEBROW_STYLE}>{'Product search'}</span>
          <h2 css={TITLE_STYLE}>{'Choose a product'}</h2>
        </div>
        <div css={SEARCH_BAR_STYLE}>
          <input
            css={SEARCH_INPUT_STYLE}
            value={searchInputValue}
            placeholder='Search by title, description, or SKU...'
            aria-label='Search products'
            autoFocus
            onChange={event => setSearchInputValue(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') search();
              if (event.key === 'Escape') onClose();
            }}
          />
          <button type='button' css={SEARCH_BUTTON_STYLE} disabled={searchInputValue.trim() === searchQuery} onClick={search}>
            {'Search'}
          </button>
        </div>
        <div css={PANES_STYLE}>
          <div css={RESULTS_STYLE}>
            {status
              ? <div css={STATUS_STYLE}>{status}</div>
              : products.map((product, index) => {
                const selected = !!selectedProduct && getProductKey(product) === getProductKey(selectedProduct);
                return (
                  <button
                    key={`${getProductKey(product)}-${index}`}
                    type='button'
                    css={[RESULT_STYLE, selected && RESULT_SELECTED_STYLE]}
                    aria-pressed={selected}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <ResultPhoto image={product.image} />
                    <span>
                      <div css={RESULT_TITLE_STYLE}>{product.title || 'Untitled product'}</div>
                      <div css={RESULT_DETAILS_STYLE}>
                        {compact([
                          getProductSearchResultSource(product),
                          product.brand,
                          product.thdStoreSku ?? product.sku,
                        ]).join(' · ')}
                      </div>
                    </span>
                    <span css={RESULT_PRICE_STYLE}>{formatUsd(product.price)}</span>
                  </button>
                );
              })}
          </div>
          <div css={DETAILS_PANE_STYLE} data-testid='focused-product-search-details'>
            <div css={DETAILS_STYLE}>
              {selectedProduct
                ? <FocusedOrgProductDetails orgProduct={selectedProduct} />
                : <div css={STATUS_STYLE}>{'Select a product to see its details'}</div>}
            </div>
            <div css={DETAILS_ACTIONS_STYLE}>
              <button
                type='button'
                css={ACTION_BUTTON_STYLE}
                disabled={!selectedProductIsAvailable || adding}
                onClick={() => selectedProduct && addProduct(selectedProduct)}
              >
                {adding ? 'Adding…' : selectedProduct && !selectedProductIsAvailable ? 'Out of stock' : currentProduct ? 'Replace product' : 'Select product'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};
